import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } else if (currentUrl) {
      // Product uses a custom template — show a lightweight variant-picker fallback
      await this.#showFallbackModal(currentUrl);
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Fallback for products whose page lacks [data-product-grid-content].
   * Fetches product JSON, builds a minimal variant-picker, and opens the modal.
   * @param {string} productPageUrl
   */
  async #showFallbackModal(productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    try {
      const url = new URL(productPageUrl);
      // Include locale prefix so Shopify returns translated option names
      const localeMatch = window.location.pathname.match(/^\/([a-z]{2,3}(?:-[a-z]{2,4})?)\//i);
      const localePrefix = localeMatch ? '/' + localeMatch[1] : '';
      const productPath = url.pathname.startsWith(localePrefix)
        ? url.pathname
        : localePrefix + url.pathname;
      const res = await fetch(url.origin + productPath + '.js');
      if (!res.ok) throw new Error('fetch failed');
      const product = await res.json();

      // ACOM: professional devices are never sold from a quick-add modal; their page runs the access check.
      if ((product.tags || []).some((tag) => String(tag).trim().toLowerCase() === 'acom-dispositivo')) {
        window.location.href = productPageUrl;
        return;
      }

      modalContent.innerHTML = this.#buildFallbackHTML(product, productPageUrl);

      const form = /** @type {HTMLFormElement | null} */ (modalContent.querySelector('[data-fallback-form]'));
      if (form) {
        const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (form.querySelectorAll('select[data-option-index]'));
        const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));

        const syncVariant = () => {
          if (!variantInput) return;
          const values = Array.from(selects).map(s => s.value);
          const match = product.variants.find(v => v.options.every((o, i) => o === values[i]));
          if (match) variantInput.value = String(match.id);
        };
        selects.forEach(s => s.addEventListener('change', syncVariant));

        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const submitBtn = /** @type {HTMLButtonElement | null} */ (form.querySelector('[data-fallback-submit]'));
          if (submitBtn) submitBtn.disabled = true;
          try {
            const variantId = variantInput?.value;
            if (!variantId) throw new Error('no variant');

            // Collect cart section IDs so Shopify returns pre-rendered HTML in the response.
            // This lets the cart component morph instantly without a second fetch.
            const cartSectionIds = Array.from(
              document.querySelectorAll('cart-items-component[data-section-id]')
            )
              .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
              .filter(Boolean);

            const requestBody = /** @type {Record<string, unknown>} */ ({
              id: Number(variantId),
              quantity: 1,
            });
            if (cartSectionIds.length) requestBody.sections = cartSectionIds;

            const cartRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify(requestBody),
            });
            if (!cartRes.ok) throw new Error('cart add failed');
            const cartData = await cartRes.json();

            document.dispatchEvent(
              new CartUpdateEvent(cartData, this.id, {
                didError: false,
                sections: cartData.sections,
              })
            );
          } catch (err) {
            console.error('Quick-add fallback submit:', err);
            if (submitBtn) submitBtn.disabled = false;
          }
        });
      }

      this.#openQuickAddModal();
    } catch {
      window.location.href = productPageUrl;
    }
  }

  /**
   * Builds a polished two-column variant-picker modal for the fallback flow.
   * @param {object} product - Product object from /products/HANDLE.js
   * @param {string} productUrl
   * @returns {string}
   */
  #buildFallbackHTML(product, productUrl) {
    const currency = /** @type {any} */ (window).Shopify?.currency?.active ?? 'EUR';
    const fmt = (cents) =>
      new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(cents / 100);
    // Product data goes into HTML: escape it, so a title or option value can never become markup.
    const esc = (value) =>
      String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

    const currentPrice = fmt(product.price_min);
    const comparePrice =
      product.compare_at_price_min > product.price_min ? fmt(product.compare_at_price_min) : null;

    const rawImg = product.featured_image;
    const imgUrl = rawImg
      ? ('https:' + rawImg).replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, (_, ext, qs) => `_600x.${ext}${qs || ''}`)
      : null;

    const optionsHTML = product.has_only_default_variant
      ? ''
      : product.options
          .map(
            (opt, i) => `
          <div class="qaf-field">
            <label class="qaf-field-label" for="qaf-opt-${i}">${esc(opt.name)}</label>
            <div class="qaf-select-wrap">
              <select class="qaf-select" id="qaf-opt-${i}" data-option-index="${i}" name="options[${esc(opt.name)}]">
                ${opt.values.map((v) => `<option value="${esc(v)}">${esc(v)}</option>`).join('')}
              </select>
              <svg class="qaf-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>`
          )
          .join('');

    const firstId = product.variants?.[0]?.id ?? '';
    const hasImage = !!imgUrl;

    return `<style>
      .qaf-root{grid-column:1/-1;display:flex;flex-direction:column;min-height:0}
      .qaf-layout{display:grid;grid-template-columns:${hasImage ? '1fr 1fr' : '1fr'};flex:1;min-height:0}
      @media(max-width:749px){.qaf-layout{grid-template-columns:1fr}}
      .qaf-media{background:#f5f3f0;display:flex;align-items:center;justify-content:center;padding:2rem;overflow:hidden}
      @media(max-width:749px){.qaf-media{max-height:200px;padding:1.25rem}}
      .qaf-img{max-width:100%;max-height:320px;width:auto;height:auto;object-fit:contain;display:block}
      .qaf-form-col{padding:2.25rem 2rem 2rem;display:flex;flex-direction:column;overflow-y:auto}
      @media(max-width:749px){.qaf-form-col{padding:1.5rem 1.25rem 1.5rem}}
      .qaf-title-link{font-size:1.25rem;font-weight:700;line-height:1.2;color:inherit;text-decoration:none;display:block;margin-bottom:.5rem;padding-right:2.5rem;transition:color .15s}
      .qaf-title-link:hover{color:#0e76bc}
      .qaf-price-row{display:flex;align-items:baseline;gap:.55rem;flex-wrap:wrap;margin-bottom:1.5rem}
      .qaf-price{font-size:1.35rem;font-weight:700;color:${comparePrice ? '#c94f39' : 'inherit'}}
      .qaf-compare{font-size:.9rem;color:rgba(0,0,0,.4);text-decoration:line-through}
      .qaf-sale-badge{font-size:.62rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;background:#c94f39;color:#fff;padding:.2em .55em;border-radius:3px;line-height:1}
      .qaf-field{margin-bottom:1.1rem}
      .qaf-field-label{display:block;font-size:.75rem;font-weight:700;text-transform:uppercase;letter-spacing:.07em;opacity:.55;margin-bottom:.45rem}
      .qaf-select-wrap{position:relative}
      .qaf-select{width:100%;padding:.7rem 2.25rem .7rem .85rem;border:1.5px solid rgba(0,0,0,.12);border-radius:8px;font-size:.95rem;font-family:inherit;appearance:none;-webkit-appearance:none;background:var(--color-background,#fff);color:inherit;cursor:pointer;transition:border-color .15s,box-shadow .15s}
      .qaf-select:hover{border-color:rgba(0,0,0,.25)}
      .qaf-select:focus{outline:none;border-color:#0e76bc;box-shadow:0 0 0 3px rgba(14,118,188,.14)}
      .qaf-chevron{position:absolute;right:.85rem;top:50%;transform:translateY(-50%);pointer-events:none;opacity:.4}
      .qaf-actions{margin-top:auto;padding-top:1.25rem}
      .qaf-btn{display:flex;align-items:center;justify-content:center;gap:.5rem;width:100%;padding:.9rem 1.5rem;background:#c94f39;color:#fff;border:none;border-radius:8px;font-size:.9rem;font-weight:700;font-family:inherit;cursor:pointer;letter-spacing:.01em;box-shadow:0 4px 14px rgba(201,79,57,.28);transition:background .18s,transform .15s,box-shadow .18s}
      .qaf-btn:hover:not(:disabled){background:#0e76bc;transform:translateY(-1px);box-shadow:0 6px 20px rgba(14,118,188,.28)}
      .qaf-btn:disabled{opacity:.55;cursor:wait;transform:none !important}
      .qaf-view-link{display:block;text-align:center;margin-top:.9rem;font-size:.8rem;color:rgba(0,0,0,.4);text-decoration:none;transition:color .15s}
      .qaf-view-link:hover{color:#0e76bc;text-decoration:underline}
    </style>
    <div class="qaf-root">
      <div class="qaf-layout">
        ${hasImage ? `<div class="qaf-media"><img src="${esc(imgUrl)}" alt="${esc(product.title)}" class="qaf-img" loading="lazy"></div>` : ''}
        <div class="qaf-form-col">
          <a href="${esc(productUrl)}" class="qaf-title-link">${esc(product.title)}</a>
          <div class="qaf-price-row">
            <span class="qaf-price">${currentPrice}</span>
            ${comparePrice ? `<s class="qaf-compare">${comparePrice}</s><span class="qaf-sale-badge">Offerta</span>` : ''}
          </div>
          <form data-fallback-form>
            <input type="hidden" name="id" value="${esc(firstId)}">
            <input type="hidden" name="quantity" value="1">
            ${optionsHTML}
            <div class="qaf-actions">
              <button data-fallback-submit type="submit" class="qaf-btn">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 4H5L5.5 7M5.5 7L6.5 11H12L13 7H5.5Z" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="7" cy="13.5" r=".8" fill="currentColor"/>
                  <circle cx="11.5" cy="13.5" r=".8" fill="currentColor"/>
                </svg>
                Aggiungi al carrello
              </button>
              <a href="${esc(productUrl)}" class="qaf-view-link">Vedi la pagina prodotto completa →</a>
            </div>
          </form>
        </div>
      </div>
    </div>`;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
